import React, { useState, useEffect, useMemo } from 'react';
import { Table, Icon, Button, message, Tooltip, Empty } from 'antd';
import { withTranslation } from 'react-i18next';

import { TableContainer, PayloadBody, FailureReasonsContainter, AnimeCloseIcon, StatusColumnContainer, StatusLabel } from './styles';
import { SEARCHABLE_TABLE, STATUS_MONITORING } from '../TranslationValues';
import { HYBRID_MAIL } from '../HybridMail/TranslationValue';
import {
  ApprovalModal,
  ControlCenter,
  dummyCallback, JobProcessingModal, JOBQueueModal,
  PrintingCenter,
  PrintingModal,
  PrintingQueue
} from '../../../../utils/complexDocumentConstants';
import DropDown from './DropDown.js';
import SearchSelectObject from '../../../../components/ReusableComponents/SearchSelectObject.js';
import ReusableModal from '../../../../components/ReusableComponents/ReusableModal.js';
import {
  base64ToPdfBlob,
  getStatusColor,
  getStatusMessage,
  transformMetaSearchFilter,
  transformMetaSearchKeys,
} from '../../../../utils/helper.js';
import {
  getJobRecordDataset,
  getJobRecords,
  getSearchMetaKeys,
  fetchExclusionsData
} from '../../../../utils/services/statusMonitoringAPI.js';
import {
  channels,
  defaultExtraColumns,
  displayDefaultColumns,
  metaSearchKeyMappings,
  printStatusLabelMapping,
  stageStatus,
  tableColumnTitle
} from '../../../../utils/constants/statusMonitoring.js';
import { getAllLanguage } from '../../../../utils/services/languageApi.js';
import URL from '../../../../utils/helper.js';
import { WarningIcon } from '../../../../images/svgIcons.js';
import { v4 as uuid } from 'uuid';

import { DashboardErrorReport } from '../../DashboardErrorReport.js';
import { deepCopy, saveByteArray } from '../../../../utils/javascript.js';
import { downloadHybridLetter, getDocument, getHybridBatchList, getSplitDocument } from '../../../../utils/services/hybridMailAPI.js';

const namespaces = ['SEARCHABLE_TABLE'];
const pageLimit = window.innerHeight < 720 ? 6 : 7;
const initialPayload = {
  page: 10,
  limit: pageLimit,
  search: [],
  sort: {
    fieldName: 'uniq_id',
    value: 'asc',
  },
};

const SearchableTable = ({
  contentType,
  t,
  statsFilter = [],
  setStatsFilter = dummyCallback,
  chartCallBack = dummyCallback,
  visible = false,
  jobId = null,
  defaultColumnCount = 4,
  statsKeys = [],
  staticStatsKeys=[],
  activeChannel='Documents',
  removedRecord,
  failureRecord,
  setRemovedRecord,
  setFailureRecord,
  exclusionHistory,
  selectedRowKeys,
  setSelectedRowKeys,
  selectedAllRows,
  setSelectedAllRows,
  setSelectedSearchFilters,
  printBatchId =undefined,
}) => {
  const [isPayloadModalVisible, setIsPayloadModalVisible] = useState(false);  
  const [payloadData, setPayloadData] = useState(null);
  const [failureData, setFailureData] = useState('');
  const [failureReasonModal,setFailureReasonModal]=useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [searchMetaKeys, setSearchMetaKeys] = useState([]);
  const [metaKeyRefs, setMetaKeyRefs] = useState({});
  const [jobRecords, setJobRecords] = useState([]);
  const [totalJobRecords, setTotalJobRecords] = useState(0);
  const [payload, setPayload] = useState(initialPayload);
  const [columnList, setColumnList] = useState(displayDefaultColumns(t, contentType, activeChannel));
  const [valueDataSet, setValueDataSet] = useState({
    [metaSearchKeyMappings(t).channel]: channels(t),
    [metaSearchKeyMappings(t).status]: stageStatus(t, activeChannel, false),// send false for email status
  });
  const allowValuesforKeys = [
    metaSearchKeyMappings(t).channel,
    metaSearchKeyMappings(t).language,
    metaSearchKeyMappings(t).status,
  ];
  const addColumn = list => {
    setColumnList(list);
  };
  const tableColumn = tableColumnTitle(t);
  const calculatedPageLimit = contentType === ApprovalModal ? 4 : pageLimit

  const [hybridSearchText, setHybridSearchText] = useState('');

  const fetchHybridRecords = async ({ status, text }) => {
  if (!visible || !jobId) return;
  try {
    const res = await getHybridBatchList({
      hybridBatchId: jobId,
      page: pageNumber,
      limit: pageLimit,
      desc: true,
      ...(status
        ? { status: status === "Hold" ? "HOLD" : "RELEASED" }
        : {}),
      ...(text ? { text } : {}),
    });

    if (res?.statusCode === 7014 && res?.data === null) {
      setJobRecords([]);                   
      setTotalJobRecords(0);
    }

    if (res?.statusCode === 7014 && res?.data?.files) {
      setJobRecords(res.data.files);                   
      setTotalJobRecords(res.dataCount ?? 0);
    }
  } catch (error) {
    console.error('Error fetching hybrid records:', error);
  }
};

useEffect(() => {
  if (activeChannel !== 'Hybrid' || !visible) return;
  const statusFilter = selectedFilters.find(
    f => f.key.toLowerCase() === t(SEARCHABLE_TABLE.STATUS.key, SEARCHABLE_TABLE.STATUS.value).toLowerCase()
  );
  fetchHybridRecords({
    status: statusFilter?.value ?? null,
    text: hybridSearchText || null,
  });
}, [selectedFilters, hybridSearchText, pageNumber, visible, activeChannel]);

  const status = useMemo(() => {
    switch (contentType) {
      case JOBQueueModal:
        return [{ fieldName: 'stage', value: 'yet_to_start' }];
      case JobProcessingModal:
      case ApprovalModal:
        return [
          {
            fieldName: 'status',
            value: activeChannel === 'Email' ? 'sent,failure' : 'success,failure',
          },
          {
            fieldName: 'channel',
            value: activeChannel === 'Documents' ? 'print' : activeChannel.toLowerCase(),
          },
        ];
      case PrintingModal:
        return failureRecord ? [{ fieldName: 'status', value: 'failure' }] : [];

      case ControlCenter:
          return [{ fieldName: 'stage', value: 'unknown' }, { fieldName: 'channel', value: activeChannel === 'Documents' ? 'print' : activeChannel.toLowerCase(), }];
      default:
        return [];
    }
  },[contentType, activeChannel, failureRecord ]);

  const printStatus = contentType === PrintingQueue ? 'in_queue' : contentType === PrintingCenter ? 'completed' : 'yet_to_print';

  const getLanguageName = (langCode) =>{
    const language= (valueDataSet[metaSearchKeyMappings(t).language]||[]).find((lang)=> lang.value === langCode);
    return language?.key || ''
  }

  let columns = [
    {
      title: tableColumn.uniq_id,
      dataIndex: 'uniqId',
      width: '10%',
      align: 'center',
      // sorter: (a, b) => a.ID - b.ID,
    },
    {
      title: tableColumn.uniq_name,
      dataIndex: 'uniqName',
      width: '25%',
      align: 'center',
      // sorter: (a, b) => a.Name - b.Name,
    },
    {
      title: tableColumn.no_of_pages,
      dataIndex: 'noOfPages',
      width: '10%',
      align: 'center',
      // sorter: (a, b) => a.BSN - b.BSN,
    },
    {
      title: tableColumn.created_on,
      dataIndex: 'createdOn',
      width: '20%',
      align: 'center',
      // sorter: (a, b) => a.BSN - b.BSN,
    },
    {
      title: tableColumn.failure_reason,
      dataIndex: 'failureReason',
      width: '20%',
      align: 'center',
      render: (text, record) => (
        <Button
          disabled={!record.failureReason}
          style={{ outline: 'none', border: 'none', background: 'none', backgroundColor: 'none' }}
          className="failure_reason"
          onClick={() => viewFailurReason(text, record)}
        >
          <Icon type="eye" />
        </Button>
      ),
    },
    {
      title: tableColumn.Payload,
      dataIndex: 'payloadId',
      width: contentType === JOBQueueModal ? '15%' : '10%',
      align: 'center',
      render: (text, record) => (
        <Button type="link" onClick={() => handleView(record.payloadId, record.channel)}>
          {t(SEARCHABLE_TABLE.VIEW_TEXT.key, SEARCHABLE_TABLE.VIEW_TEXT.value)}
        </Button>
      ),
      // ...(contentType === JOBQueueModal && {fixed: 'right',
      //   onHeaderCell: () => ({
      //     style: { paddingRight: '55px', background: 'rgb(246, 246, 246)' },
      //   })})
    },
  ];

  if(contentType === JOBQueueModal){
    columns.splice(2, 0, 
    {
      title: tableColumn.channel,
      dataIndex: 'channel',
      width: '15%',
      align: 'center',
      render: channel => {
        const item = channels(t).find(entry => entry.value === channel);
        return item ? item.key : null;
      }
      // sorter: (a, b) => a.ID - b.ID,
    });
  }

  if([JobProcessingModal, ApprovalModal, PrintingModal].includes(contentType)){
    columns.splice(3, 0, {
      title: tableColumn.status,
      dataIndex: 'status',
      width: '20%',
      align: 'center',
      // sorter: (a, b) => a.Status - b.Status,
      render: Status => {
        const isStaticSuccess = (contentType === PrintingModal && !failureRecord);
        return (
          <StatusColumnContainer>
            <StatusLabel
              backgroundColor={getStatusColor(isStaticSuccess ? 'success' : Status)}
              color={
                ['failure', 'clicks'].includes(isStaticSuccess ? 'success' : Status)
                  ? 'white'
                  : 'black'
              }
            >
              {isStaticSuccess
                ? t(STATUS_MONITORING.SUCCESS.key, STATUS_MONITORING.SUCCESS.value)
                : Status && getStatusMessage(Status, t).toUpperCase()}
            </StatusLabel>
          </StatusColumnContainer>
        );
      },
    });
  }

  if([PrintingQueue, PrintingCenter].includes(contentType)){
    columns.splice(3, 0, {
      title: tableColumn.printStatus,
      dataIndex: 'printStatus',
      width: '20%',
      align: 'center',
      // sorter: (a, b) => a.Status - b.Status,
      render: printStatus => {
        return (
          <StatusColumnContainer>
            <StatusLabel
              backgroundColor={getStatusColor(printStatusLabelMapping[printStatus])}
              color={
                ['yet_to_print'].includes(printStatusLabelMapping[printStatus])
                  ? 'white'
                  : 'black'
              }
            >
              {getStatusMessage(printStatusLabelMapping[printStatus], t).toUpperCase()}
            </StatusLabel>
          </StatusColumnContainer>
        );
      },
    });
  }

  if (searchMetaKeys.length > 0) {
    const metaKeys = [
      ...new Set([
        ...defaultExtraColumns(t, contentType, activeChannel, failureRecord),
        ...displayDefaultColumns(t, contentType, activeChannel),
      ]),
    ];

    let count = 0;
    searchMetaKeys.forEach((key,i) => {
      if (!metaKeys.includes(key)) {
        count++;
        const position = contentType === JOBQueueModal ? (key === tableColumn.language ? 3 : 5+count) : (key === tableColumn.language ? 2 : 4+count)
        columns.splice(position, 0, {
          title: key,
          dataIndex: metaKeyRefs[key],
          width: '25%',
          align: 'center',
          render: (text, record) => key === tableColumn.language ?  getLanguageName(record.language) : text,
        });
      }
    });
  }

  const handlePdfView = async (record) => {
    if (record?.env && record?.docURL) {
      const documentURL = record.docURL.replace(/^\//, '');
      const url = `${URL.TRANSACTION_SYSTEM_URL}${documentURL}`;
      window.open(url, '_blank');
    } else {
      console.error("Invalid record object: Missing 'env' or 'docURL'.");
    }
  };

  const handleEmailView = async (record) => {
    window.open(`/EmailViewer/${record.payloadId}`, '_blank');
  }

  const handleHybridLetterView = async (record) => {
    try {
      const newTab = window.open();

      // Create loading UI safely
      const doc = newTab.document;

      doc.title = t(SEARCHABLE_TABLE.LOADING_PDF.key, SEARCHABLE_TABLE.LOADING_PDF.value);

      const container = doc.createElement('div');
      container.style.display = 'flex';
      container.style.justifyContent = 'center';
      container.style.alignItems = 'center';
      container.style.height = '100vh';
      container.style.fontFamily = 'sans-serif';

      container.innerHTML = `
      <div style="text-align:center">
        <h2>${t(SEARCHABLE_TABLE.LOADING_DOCUMENT.key, SEARCHABLE_TABLE.LOADING_DOCUMENT.value)}</h2>
        <p>${t(SEARCHABLE_TABLE.PLEASE_WAIT.key, SEARCHABLE_TABLE.PLEASE_WAIT.value)}</p>
      </div>
    `;

      doc.body.appendChild(container);

      const byteArray = await downloadHybridLetter(record?.finalDownloadUrl);

      const url = saveByteArray(`${record.finalFileName}`, byteArray, false);

      newTab.location.href = url;
    } catch (error) {
      console.error(error);
    }
  };

  if (contentType !== JOBQueueModal) {
    const warningIcon = (tooltipKey, tooltipValue) => (
      <Tooltip placement="top" title={t(tooltipKey, tooltipValue)}>
        <span style={{ display: 'inline-block' }}>
          <WarningIcon height={25} width={25} />
        </span>
      </Tooltip>
    );

    const columnConfig = {
      Documents: {
        title: tableColumn.GeneratedPDF,
        dataIndex: 'GeneratedPDF',
        isSuccess: (record) =>
          record.status === 'success' ||
          [PrintingModal, PrintingQueue, PrintingCenter].includes(contentType),
        onClick: handlePdfView,
        buttonText: t(SEARCHABLE_TABLE.VIEW_TEXT.key, SEARCHABLE_TABLE.VIEW_TEXT.value),
        failureTooltip: SEARCHABLE_TABLE.GENERATE_PDF_FAILED,
      },

      Email: {
        title: tableColumn.ViewEmail,
        dataIndex: 'ViewEmail',
        isSuccess: (record) => record.status === 'sent',
        onClick: handleEmailView,
        buttonText: SEARCHABLE_TABLE.SHOW_EMAIL,
        failureTooltip: SEARCHABLE_TABLE.VIEW_EMAIL_FAILED,
      },

      Hybrid: {
        title: tableColumn.GeneratedLetter,
        dataIndex: 'GeneratedLetter',
        isSuccess: (record) => record.status === 'success',
        onClick: handlePdfView,
        buttonText: t(SEARCHABLE_TABLE.VIEW_TEXT.key, SEARCHABLE_TABLE.VIEW_TEXT.value),
        failureTooltip: SEARCHABLE_TABLE.GENERATE_LETTER_FAILED,
      },
    };

    const config = columnConfig[activeChannel];

    if (config) {
      columns.push({
        title: config.title,
        dataIndex: config.dataIndex,
        width: '20%',
        align: 'center',
        render: (_, record) =>
          config.isSuccess(record) ? (
            <Button type="link" onClick={() => typeof config.onClick === 'function' && config.onClick(record)}>
              {typeof config.buttonText === 'string'
                ? config.buttonText
                : t(config.buttonText.key, config.buttonText.value)}
            </Button>
          ) : (
            warningIcon(
              config.failureTooltip.key,
              config.failureTooltip.value
            )
          ),
      });
    }
  }

  if (contentType === PrintingQueue) {
    columns.push({
      title: tableColumn.pending_status,
      dataIndex: 'Pending_status',
      width: '15%',
      align: 'center',
      render: () => {
        return <div> {t(SEARCHABLE_TABLE.PRINTING_LABEL.key, SEARCHABLE_TABLE.PRINTING_LABEL.value)}</div>;
      },
    });
  };

  if (activeChannel === 'Hybrid') {
    columns = [
      {
        title: 'File Name',
        dataIndex: 'splittedFileName',
        width: '20%',
        align: 'center',
      },
      {
        title: 'Address',
        dataIndex: 'addressBlock',
        width: '20%',
        align: 'center',
        render: (addressBlock) => addressBlock?.text || '',  // ✅ extract text
      },
      {
        title: 'Start Page',
        dataIndex: 'startPage',
        width: '10%',
        align: 'center',
      },
      {
        title: 'End Page',
        dataIndex: 'endPage',
        width: '10%',
        align: 'center',
      },
      {
        title: tableColumn.status,
        dataIndex: 'status',
        width: '20%',
        align: 'center',
        render: (status) => (
          <StatusColumnContainer>
            <StatusLabel
              backgroundColor={status === "HOLD" ? "#ff4d4f" : "#52c41a"}
              color="white"
            >
              {status}
            </StatusLabel>
          </StatusColumnContainer>
        ),
      },
      {
        title: tableColumn.GeneratedLetter,
        dataIndex: 'finalDownloadUrl',
        width: '20%',
        align: 'center',
        render: (finalDownloadUrl, record) =>
          finalDownloadUrl ? (
            <Button
              type="link"
              disabled={record.status === "GENERATION_FAILED" || record.status === "GENERATION_NOT_STARTED"}
              onClick={() => handleHybridLetterView(record)}
            >
              {t(SEARCHABLE_TABLE.VIEW_TEXT.key, SEARCHABLE_TABLE.VIEW_TEXT.value)}
            </Button>
          ) : (
            <Tooltip placement="top" title={t(SEARCHABLE_TABLE.LETTER_GENERATION_FAILED.key, SEARCHABLE_TABLE.LETTER_GENERATION_FAILED.value)}>
              <span style={{ display: 'inline-block' }}>
                <WarningIcon height={25} width={25} />
              </span>
            </Tooltip>
          ),
      },
    ];
  }

  const scrollX = columnList.length > defaultColumnCount ? columnList.length * 150 : undefined;

  const updatedColumns = columns
    .filter(list => columnList.includes(list.key || list.title))
    .map(col => ({
      ...col,
      onCell: record => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    }));

  const handleView = async (transactionId, channel) => {
    const res = await getJobRecordDataset(transactionId, channel);
    if (res.statusCode === 5008 && res.data) {
      if (res.data.payload) {
        const dataSet = JSON.parse(res.data.payload);
        setPayloadData(dataSet);
        setIsPayloadModalVisible(true);
      }
    }
  };

  const viewFailurReason = (text, record)=>{
    setFailureReasonModal(true);
    setFailureData(record.failureDescription);
  };

  const fetchJobRecords = async payloadInfo => {
    if (visible && jobId) {
      try {
        const res = removedRecord ? await fetchExclusionsData(payloadInfo) : await getJobRecords(payloadInfo);
        if (res.statusCode === 5004 && res.data) {
          setJobRecords(res.data.map(item => ({
            ...item,
            ...item.metadataFields
          })));
          if(selectedAllRows){
            setSelectedRowKeys((res.data ||[]).map(item => item.transactionSearchId));
          }
          setTotalJobRecords(res.dataCount);
        }
      } catch (error) {
        console.error('Error fetching meta search keys:', error);
      }
    }
  };

  const transformedMetaSearchFilter = useMemo(() => {
    setPageNumber(1);

    if (activeChannel === 'Hybrid') {
      const statusKey = t(SEARCHABLE_TABLE.STATUS.key, SEARCHABLE_TABLE.STATUS.value);
      const hybridFilters = selectedFilters
        .filter(f => f.key.toLowerCase() === statusKey.toLowerCase())
        .map(f => ({ fieldName: 'status', value: f.value }));
      return hybridFilters;
    }

    return transformMetaSearchFilter(
      [...selectedFilters, ...statsFilter],
      metaKeyRefs,
      valueDataSet,
    );
  }, [selectedFilters, statsFilter, removedRecord, failureRecord, activeChannel]);

  const computedPayload = useMemo(() => {
    if (!visible) return { ...payload, search: transformedMetaSearchFilter, page: pageNumber };
    let defaultStatus = [...status];
    const includeIndex = transformedMetaSearchFilter.findIndex(item =>
      item.fieldName.includes('status')
    );
    if (includeIndex !== -1) {
      defaultStatus = defaultStatus.filter(
        item => !['success', 'sent', 'failure'].some(status => item.value.includes(status))
      );
    }
    const searchData = [...transformedMetaSearchFilter, ...defaultStatus];
    const exclusions = contentType === PrintingModal ? exclusionHistory ?? [] : [];
    const updatedPayload = deepCopy(payload);
    if ((contentType === PrintingModal && (failureRecord || removedRecord))) {
      delete updatedPayload.printStatus;
    }
    return {
      ...updatedPayload,
      jobId,
      search: searchData,
      exclusions: exclusions,
      page: pageNumber,
      ...(contentType === ApprovalModal ? { limit: 4 } : {}),
      ...(([PrintingModal, PrintingQueue, PrintingCenter].includes(contentType) && !failureRecord) ? {printStatus:printStatus}:{}),
      ...((printBatchId && [PrintingQueue, PrintingCenter].includes(contentType)) ? {printBatchId: printBatchId} : {}),
    };
  }, [transformedMetaSearchFilter, pageNumber, activeChannel, exclusionHistory,removedRecord, failureRecord, printBatchId]);


  useEffect(() => {
    if (visible) {
      const getMetaSearchKeys = async () => {
        if (visible && jobId) {
          try {
            const res = await getSearchMetaKeys(jobId);
            if (res.statusCode === 5002) {
              const metaKey = (res.data && res.data.length > 0) ? [
                ...new Set([...staticStatsKeys, ...res.data])] : staticStatsKeys;
              const { transformedKeys, referenceKeysObject } = transformMetaSearchKeys(metaKey, t);
              setSearchMetaKeys(transformedKeys);
              setMetaKeyRefs({...referenceKeysObject});
            }
          } catch (error) {
            console.error('Error fetching meta search keys:', error);
          }
        }
      };

      const fetchLanguages = async () => {
        if (visible && jobId) {
          try {
            const res = await getAllLanguage();
            if (res.statusCode === 109 && res.data) {
              const lang = res.data.map(lang => ({ key: lang.name, value: lang.code }));
              setValueDataSet(prev => ({ ...prev, [metaSearchKeyMappings(t).language]: lang }));
            }
          } catch (error) {
            console.error('Error fetching meta search keys:', error);
          }
        }
      };

      getMetaSearchKeys();
      fetchLanguages();
    }
  }, [visible, jobId, activeChannel]);

  useEffect(() => {
    if (visible && activeChannel !== 'Hybrid') {
      setPayload(computedPayload);
      fetchJobRecords(computedPayload);
    }
  }, [computedPayload, visible, jobId, removedRecord]);

  useEffect(() => {
    selectedFilters.length && setSelectedFilters([]);
    jobRecords.length && setJobRecords([]);
  }, [activeChannel,removedRecord])

  useEffect(() => {
    setSelectedSearchFilters && setSelectedSearchFilters([...transformedMetaSearchFilter, ...status]);
  }, [transformedMetaSearchFilter]);



  const handleCopy = () => {
    if (payloadData) {
      const jsonString = JSON.stringify(payloadData, null, 2);
      navigator.clipboard
        .writeText(jsonString)
        .then(() => {
          message.success(
            t(
              SEARCHABLE_TABLE.PAYLOAD_COPIED_SUCCESS_MESSAGE.key,
              SEARCHABLE_TABLE.PAYLOAD_COPIED_SUCCESS_MESSAGE.value,
            ),
          );
        })
        .catch(() => {
          message.error(
            t(
              SEARCHABLE_TABLE.FAILED_TO_COPY_PAYLOAD.key,
              SEARCHABLE_TABLE.FAILED_TO_COPY_PAYLOAD.value,
            ),
          );
        });
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onSelect: (selected, selectedRows) => {
      if(selectedRows) {
        setSelectedRowKeys(prev => [...prev, selected?.transactionSearchId]);
      } else {
        setSelectedRowKeys(prev => prev.filter(recordId => recordId !== selected?.transactionSearchId));
      }
    },
    onSelectAll: (selected) => {
      if (selected) {
        setSelectedRowKeys(jobRecords.map((item) => item.transactionSearchId));
        setSelectedAllRows(true);
      } else {
        setSelectedRowKeys([]);
        setSelectedAllRows(false);
      }
    },
    columnWidth: '6%',
  };


  const defaultColumn = defaultExtraColumns(t, contentType, activeChannel, failureRecord);

  const ArrowLeftIcon = () => {
    return (
      <AnimeCloseIcon
        onClick={() => {
          setRemovedRecord(false);
          setFailureRecord(false);
        }}
      >
        <Tooltip title={t(SEARCHABLE_TABLE.BACK_TOOLTIP.key, SEARCHABLE_TABLE.BACK_TOOLTIP.value)} placement="left">
          <img src="arrowLeftCircle.svg" width="26px" height="26px" />{' '}
        </Tooltip>
      </AnimeCloseIcon>
    );
  };

  const searchMetaKeysData = searchMetaKeys.length && contentType === PrintingModal 
  ? [...searchMetaKeys.slice(0, 3), metaSearchKeyMappings(t).status, ...searchMetaKeys.slice(3)]
  : [...searchMetaKeys];

  const tableMessages = useMemo(() => {
    const hasSearchFilter = transformedMetaSearchFilter.length > 0;
    const emptySearchMessage = t(SEARCHABLE_TABLE.NO_DATA_FOR_SEARCH.key, SEARCHABLE_TABLE.NO_DATA_FOR_SEARCH.value)
    const commonTypes = [
      JOBQueueModal,
      JobProcessingModal,
      ApprovalModal,
      PrintingQueue,
      PrintingCenter,
      ControlCenter,
    ];
  
    const isCommonType = commonTypes.includes(contentType);
    const isPrintingModal = contentType === PrintingModal;
  
    if (!totalJobRecords.length) {
      if (isCommonType) {
        return hasSearchFilter ? emptySearchMessage : t(SEARCHABLE_TABLE.NO_RECORDS_FOUND.key, SEARCHABLE_TABLE.NO_RECORDS_FOUND.value);
      }
  
      if (isPrintingModal) {
        const hasRemovedOrFailure = removedRecord || failureRecord;
        if (hasSearchFilter) {
          return emptySearchMessage;
        }
        return hasRemovedOrFailure ? t(SEARCHABLE_TABLE.NO_RECORDS_FOUND.key, SEARCHABLE_TABLE.NO_RECORDS_FOUND.value) : t(SEARCHABLE_TABLE.ALL_SENT_FOR_PRINTING.key, SEARCHABLE_TABLE.ALL_SENT_FOR_PRINTING.value);
      }
  
      return t(SEARCHABLE_TABLE.NO_RECORDS_FOUND.key, SEARCHABLE_TABLE.NO_RECORDS_FOUND.value);
    }
  
    return t(SEARCHABLE_TABLE.NO_RECORDS_FOUND.key, SEARCHABLE_TABLE.NO_RECORDS_FOUND.value);
  }, [contentType, totalJobRecords.length, transformedMetaSearchFilter.length, removedRecord, failureRecord]);


  return (
    <TableContainer selectedAll={selectedAllRows}>
      {(removedRecord || failureRecord) && (
        <div
          className="remove-view-record"
          style={{
            alignItems: 'center',
            fontFamily: 'lato',
            fontSize: '16px',
            fontWeight: 'bolder',
            color: 'black',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '30px',
          }}
        >
          <div style={{ cursor: 'pointer' }}>
            <ArrowLeftIcon />
          </div>
          <div>{failureRecord ? t(
              SEARCHABLE_TABLE.FAILURE_RECORDS.key,
              SEARCHABLE_TABLE.FAILURE_RECORDS.value,
            ) :  removedRecord ? t(
              SEARCHABLE_TABLE.REMOVED_RECORDS.key,
              SEARCHABLE_TABLE.REMOVED_RECORDS.value,
            ) : null}</div>
          <div></div>
        </div>
      )}

      <div className="filterContainer">

        <SearchSelectObject
          keyDataSet={activeChannel === 'Hybrid' ? [t(SEARCHABLE_TABLE.STATUS.key, SEARCHABLE_TABLE.STATUS.value)] : searchMetaKeys}
          placeholder={t(
            SEARCHABLE_TABLE.SEARCH_METADATA_PLACEHOLDER.key,
            SEARCHABLE_TABLE.SEARCH_METADATA_PLACEHOLDER.value,
          )}
          showSelectedKeys={false}
          isDynamicKeyValue={true}
          selectedFilters={selectedFilters}
          selectedStatsFilter={statsFilter}
          setSelectedFilters={setSelectedFilters}
          chartCallBack={activeChannel === 'Hybrid' ? undefined : chartCallBack}
          statsKeys={activeChannel === 'Hybrid' ? [] : statsKeys}
          setStatsFilter={setStatsFilter}
          valueDataSet={
            activeChannel === 'Hybrid'
              ? {
                [t(SEARCHABLE_TABLE.STATUS.key, SEARCHABLE_TABLE.STATUS.value)]: [
                  { key: 'Hold', value: 'HOLD' },
                  { key: 'Released', value: 'RELEASED' },
                ],
              }
              : valueDataSet
          }
          allowValuesforKeys={
            activeChannel === 'Hybrid'
              ? [t(SEARCHABLE_TABLE.STATUS.key, SEARCHABLE_TABLE.STATUS.value)]
              : allowValuesforKeys
          }
          setSelectedAllRows={setSelectedAllRows}
          contentType={contentType}
          onFreeTextSearch={activeChannel === 'Hybrid' ? setHybridSearchText : undefined}
        />
        {contentType === PrintingModal && !removedRecord && !failureRecord && (
          <div className="docCount">
            <div className="desc">
              {t(
                SEARCHABLE_TABLE.SELECTED_DOCUMENT_LABEL.key,
                SEARCHABLE_TABLE.SELECTED_DOCUMENT_LABEL.value,
              )}
            </div>{' '}
            <div className="count"> {selectedAllRows ? totalJobRecords : selectedRowKeys?.length}</div>
          </div>
        )}
        {/* {![JOBQueueModal].includes(contentType) && (
          <div className="rightSideFilter">
            {statsFilter?.map(list => {
              return (
                <div className="singleFilter">
                  {list.label} &nbsp;{' '}
                  <Icon
                    type="close"
                    onClick={() => {
                      clearStatus(list);
                    }}
                  />
                </div>
              );
            })}
            <div className="clearBtn" onClick={() => {}}>
              <u>
                {t(
                  SEARCHABLE_TABLE.CLEAR_FILTER_BUTTON.key,
                  SEARCHABLE_TABLE.CLEAR_FILTER_BUTTON.value,
                )}
              </u>
            </div>
          </div>
        )} */}
      </div>
      <div className="table-wrapper" style={{ position: 'relative', height: 'calc(100% - 50px)' }}>
        <Table
          size={'small'}
          dataSource={jobRecords || []}
          columns={updatedColumns}
          className="pagination-container"
          scroll={{ x: scrollX }}
          onChange={pagination => setPageNumber(pagination.current)}
          rowKey={record => record.transactionSearchId || uuid()}
          pagination={{
            defaultPageSize: calculatedPageLimit,
            hideOnSinglePage: true,
            total: totalJobRecords,
            current:pageNumber
          }}
          rowSelection={contentType === PrintingModal && !removedRecord && !failureRecord ? rowSelection : null}
          locale={{ emptyText: <Empty description={tableMessages}></Empty> }}
        />
        <div
          style={{
            position: 'absolute',
            top: '4px',
            right: '1px',
            backgroundColor: '#f6f6f6',
            display: 'flex',
            width: '30px',
            height: 'auto',
          }}
        >
          {activeChannel !== 'Hybrid' && (
            <DropDown
              addColumn={addColumn}
              keyDataSet={[...new Set([...searchMetaKeysData, ...defaultColumn])]}
              defaultSelectedColumns={displayDefaultColumns(t, contentType, activeChannel)}
            />
          )}
        </div>
      </div>
      <ReusableModal
        visible={isPayloadModalVisible}
        title={t(
          SEARCHABLE_TABLE.PAYLOAD_MODAL_TITLE.key,
          SEARCHABLE_TABLE.PAYLOAD_MODAL_TITLE.value,
        )}
        onCancel={() => setIsPayloadModalVisible(false)}
        width="40%"
        styleProps={{
          top: 'auto',
          margin: '0 auto',
        }}
        bodyStyleProps={{
          padding: '0px 20px 20px 20px',
          overflow: 'auto',
        }}
        footer={[
          <Button key="copy" onClick={handleCopy} hidden={!payloadData}>
            {t(
              SEARCHABLE_TABLE.COPY_PAYLOAD_BUTTON.key,
              SEARCHABLE_TABLE.COPY_PAYLOAD_BUTTON.value,
            )}
          </Button>,
        ]}
        centered
        maskClosable={true}
        // className="job-queue right-aligned-modal"
        // transitionName={'ant-modal-slide-in'}
      >
        {payloadData && (
          <PayloadBody className="payload-body">
            <code>{JSON.stringify(payloadData, null, 4)}</code>
          </PayloadBody>
        )}
      </ReusableModal>

      <ReusableModal
        visible={failureReasonModal}
        title={tableColumn.failure_reason}
        onCancel={() => setFailureReasonModal(false)}
        width="35%"
        styleProps={{
          top: 'auto',
          margin: '0 auto',
        }}
        bodyStyleProps={{
          padding: '0px 20px 20px 20px',
          overflow: 'auto',
        }}
        centered
        maskClosable={true}
      >
        <FailureReasonsContainter>
          <DashboardErrorReport failureDetails={failureData} />
        </FailureReasonsContainter>
      </ReusableModal>
    </TableContainer>
  );
};

export default withTranslation(namespaces)(SearchableTable);
